import { ListItemText } from "@mui/material";
import { CoreMenuItem } from "@verity/components/core";
import i18n from "../../../i18n";
import type { PDFReportGeneratorState } from ".";

export type PDFReportMenuItemsProps = {
	state: PDFReportGeneratorState;
	buttonText: string;
	onButtonClick: () => void;
	onRerender: () => void;
	/**
	 * MenuList's autoFocusItem clones this onto whichever child it thinks is first — but since this
	 * component wraps its items in a Fragment, that lands on this component itself rather than directly
	 * on our first CoreMenuItem, so we relay it down ourselves.
	 */
	autoFocus?: boolean;
};

/**
 * The "Generate/Download Report" + "Regenerate Report" pair, presented as menu items instead of the
 * standalone button pair PDFGenerator renders. Purely presentational — the usePDFReportGenerator state
 * machine itself lives in EventsDownloadMenu (which stays mounted for the page's lifetime), not here,
 * since this component only exists while the kebab menu is open and would otherwise lose progress every
 * time the menu closes and reopens.
 * Rendered as a single fragment (not split up by the caller) so "Regenerate Report" always stays
 * immediately below its "Download Report" item, never interleaved with unrelated menu items.
 */
export default function PDFReportMenuItems({
	state,
	buttonText,
	onButtonClick,
	onRerender,
	autoFocus,
}: PDFReportMenuItemsProps) {
	const disabled = state === "generating" || state === "downloading";
	return (
		<>
			<CoreMenuItem
				onClick={onButtonClick}
				disabled={disabled}
				// Disabled elements can't take focus — MUI's own MenuList would've skipped to the next
				// non-disabled item and focused that instead, so mirror that here rather than dropping focus.
				autoFocus={autoFocus && !disabled}
			>
				<ListItemText>{disabled ? `${buttonText}...` : buttonText}</ListItemText>
			</CoreMenuItem>
			{state === "downloadReady" && (
				<CoreMenuItem onClick={onRerender}>
					<ListItemText>
						{i18n.t("result.components.resultsPage.proctoringResultsReport.regenerateReport")}
					</ListItemText>
				</CoreMenuItem>
			)}
		</>
	);
}
